import { FC, useState, useCallback, useEffect, useMemo } from 'react';

import { disableProductRegistration, isConstruction } from 'config';
import { useAppDispatch } from 'hooks/redux';
import { useTranslations } from 'hooks/useTranslations';
import { MenuItemVisibility } from 'models/layout';
import * as routes from 'router/routes';
import { logout } from 'store/authState';
import { logoPaths } from 'styles';
import {
  externalMenuLinksHusqvarna,
  externalMenuLinksConstruction,
  menuLinks
} from 'utils/constants';
import { pushGTMEvent } from 'utils/analyticsUtils';
import HamburgerButton from 'components/UI/Buttons/HamburgerButton';
import Drawer from 'components/UI/Drawer';

import arrowRightIcon from 'assets/vectors/arrow-right.svg';

import {
  NavbarMargin,
  NavbarContainer,
  NavbarCol,
  Logo,
  LogoLink,
  MenuGrid,
  LinkRow,
  LinkExternal,
  ActionRow,
  ArrowIcon,
  NavbarInner
} from './styled';

type Props = {
  authenticated: boolean;
};

const Navbar: FC<Props> = ({ authenticated }) => {
  const t = useTranslations();
  const dispatch = useAppDispatch();

  // State
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);

  // Logout
  const onLogout = useCallback(() => {
    if (authenticated) {
      pushGTMEvent('logoutClick');
      dispatch(logout());
    }
    setMenuIsOpen(false);
  }, [dispatch, authenticated]);

  // Actions
  const onCloseMenu = useCallback(() => setMenuIsOpen(false), []);

  // Hide menu item
  const hideMenuItem = useCallback(
    (visibility: MenuItemVisibility) => {
      switch (visibility) {
        case MenuItemVisibility.Private:
          return !authenticated;
        case MenuItemVisibility.Public:
          return authenticated;
        default:
          return false;
      }
    },
    [authenticated]
  );

  // useEffect
  // Lock scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuIsOpen ? 'hidden' : 'visible';
  }, [menuIsOpen]);

  const externalMenuLinks = useMemo(
    () =>
      isConstruction
        ? externalMenuLinksConstruction
        : externalMenuLinksHusqvarna,
    []
  );

  return (
    <>
      <NavbarMargin />
      <NavbarContainer>
        <NavbarInner>
          <LogoLink to={routes.ROOT}>
            <Logo src={logoPaths.husqvarna} alt="Logo" />
          </LogoLink>
          <NavbarCol $alignRight>
            <HamburgerButton setIsOpen={setMenuIsOpen} isOpen={menuIsOpen} />
          </NavbarCol>
        </NavbarInner>
      </NavbarContainer>
      <Drawer
        isOpen={menuIsOpen}
        onClose={onCloseMenu}
        title={t('menu.title')}
        position="right"
      >
        <MenuGrid>
          {menuLinks.map(({ visibility, name, link }) => {
            // Hide menu item
            if (hideMenuItem(visibility)) {
              return null;
            }

            // Disable product registration link
            if (
              disableProductRegistration &&
              name === 'menu.product_registration'
            ) {
              return null;
            }

            return (
              <LinkRow to={link} key={name} onClick={onCloseMenu}>
                {t(name)}
                <ArrowIcon src={arrowRightIcon} alt="arrow right" />
              </LinkRow>
            );
          })}
          {authenticated && (
            <ActionRow onClick={onLogout}>{t('menu.logout')}</ActionRow>
          )}
        </MenuGrid>
        <MenuGrid>
          {externalMenuLinks.map(({ name, link }) => (
            <LinkExternal
              key={name}
              href={link}
              target="_blank"
              rel="noreferrer"
            >
              {t(name)}
            </LinkExternal>
          ))}
        </MenuGrid>
      </Drawer>
    </>
  );
};

export default Navbar;
